/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component, FormEvent } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { message, Checkbox } from 'antd';
import Link from '../../_shared/link';
import { Sidebar } from '../equipment-details/sidebar.component';
import FormTitle from '../../_shared/form-title';
import { Equipment, _Equipment } from '../../_shared/interfaces/equipment';
import { Org } from '../../_shared/interfaces/org';
import { Model } from '../../_shared/interfaces/model';
import {
  buildErrorMsgFromForm,
  findServiceInTypes,
  getEquipConfig,
  getEquipMaint,
  userHasRole,
  isHandpiece,
  handpieceIsDueForService,
  stopProp,
} from '../../utils';
import { AppState } from '../../app.state';
import { get, set, size, flatten, uniq } from 'lodash';
import SharedStyles from '../../_shared/styles';
import styles from './manage-service-record.styles';
import { Button } from '../../_shared/button';
import {
  serviceType,
  SERIAL,
  SERIALNOTNECESSARY,
  IServiceType,
} from '../../_shared/lib/services';
import moment from 'moment';
import { _Service, Service } from '../../_shared/interfaces/service';
import { createService } from '../../_shared/services/manage-services.service';
import { createOrUpdateEquipment } from '../../_shared/services/manage-equipment.service';
import { sendDeviceCommand } from '../../_shared/services/manage-schedule.service';
import { WithForm } from '../../_shared/form';
import { WithAppConfig, AppConfig } from '../../_shared/hooks/useAppConfig';
import { trackEvent, } from '../../_shared/services/analytics.service';
import eventTypes from '../../_shared/interfaces/api/eventTypes';

interface IProps extends RouteComponentProps {
  equipment: Equipment;
  serviceOrg: Org;
  dentalOrg: Org;
  model: Model;
  sState: AppState;
  serviceTypes: IServiceType[];
  form?: any;
  app?: AppConfig;
  modal?: any;
}

const NOKEY = '_noKey_';

export class _ManageServiceRecordComponent extends Component<IProps> {
  state = {
    loading: false,
    equipment: undefined,
    serviceOrg: undefined,
    dentalOrg: undefined,
    model: undefined,
    serviceRecord: undefined,
    editMode: true,
    acceptDisclaimer: true,
    toDisplay: (opts = {}) => [
      {
        title: 'Technician Name',
        var: 'techName',
        canDisplay: () => true,
        opts: [],
        style: `
          float: left;
          width: 62%;
          margin-right: 10px;
        `,
        options: {
          rules: [
            {
              max: 50,
              message: 'Technician Name is limited to 50 characters. ',
            },
          ],
        },
        props: {
          disabled: !get(opts, 'editMode'),
        }
      },
      {
        title: 'Date',
        var: 'serviceDate',
        type: 'datepicker',
        style: `
          float: left;
        `,
        options: {
          rules: [
            {
              required: true,
              message: 'Date is required. ',
            },
          ],
        },
        props: {
          disabled: !get(opts, 'editMode'),
        }
      },
      {
        title: 'Note',
        var: 'note',
        type: 'textarea',
        placeholder: 'Notes...',
        style: `clear: both;`,
        options: {
          rules: [
            {
              max: 250,
              message: 'Note is limited to 250 characters. ',
            },
          ],
        },
        props: {
          disabled: !get(opts, 'editMode'),
        }
      },
      {
        title: 'Services',
        var: 'services',
        type: 'AutoComplete',
        hide: [],
        // AER-1465: guard against location.state being undefined (deep-link
        // / refresh from QA can blank-screen the page otherwise).
        selected: size(get(this.props, 'location.state.serviceTypes'))
          ? (get(this.props, 'location.state.serviceTypes', []) as { name: string }[]).map((s: { name: string }) => s.name)
          : [],
        data: () => {
          const { model, toDisplay } = this.state;
          const selected = get(
            toDisplay().find(td => td.var === 'services'),
            'selected',
            []
          ) as string[];

          const options = [];
          const heads: any = get(model, 'heads');
          const modelType: any = get(model, 'type');
          if (modelType) {
            for (let k in serviceType) {
              const st = serviceType[k];
              if (st.type === modelType || st.type === 'both') {
                if (st.heads && heads) {
                  if (heads >= st.heads || typeof st.heads === 'string') {
                    options.push({ text: st.name, value: k });
                  }
                } else if (get(st, 'selectable', true)) {
                  options.push({ text: st.name, value: k });
                }
              }
            }
          }

          return options.filter(o => !selected.find(s => s == o.text));
        },
        props: {
          disabled: !get(opts, 'editMode'),
        }
      },
      ...get(opts, 'services', []).map((item, i) => {
        const st = findServiceInTypes(item);

        if (st && st.showSerial) {
          return {
            // AER-1696: `item` is the raw form value, which for a service the
            // user just picked is the catalog key (`comp_desdryers`), so the
            // label read "Serial - comp_desdryers". Show the friendly name off
            // the already-resolved service type instead.
            // AER-1733: `var` used to key off `item` as well, but the raw form
            // value has no single shape — the maintenance-alert seed wrote
            // friendly names (`serial_Desiccant Tower`) and the edit-path seed
            // writes `{value, text}` objects (`serial_[object Object]`), while
            // both the serial seeding in `setFieldsOriginal` and submit
            // (`${SERIAL}${get(st, 'key')}`) address the field by catalog key.
            // A stored serial therefore landed in a field that was never on
            // screen, and re-typing it saved nothing. `findServiceInTypes`
            // already normalises a key, a name or a `{value}`/`{key}` object
            // down to the catalog key, so key the field off that: the write
            // side now matches the read side whatever the form value holds,
            // including any seeding channel added later.
            title: `Serial - ${get(st, 'name', item)}`,
            var: `${SERIAL}${get(st, 'key', item)}`,
            options: {
              rules: [
                {
                  required: true,
                  message: 'Serial number is required. ',
                },
                {
                  max: 20,
                  message:
                    'Serial number is limited to 20 characters. ',
                },
              ],
            },
            props: {
              disabled: !get(opts, 'editMode'),
            }
          }
        }

        return null;
        
        // return st && st.showSerial ? (
        //   <Form.Item key={i} label={`Serial - ${item}`}>
        //     {getFieldDecorator(`${SERIAL}${item}`, {
        //       rules: [
        //         {
        //           required: true,
        //           message: 'Serial number is required. ',
        //         },
        //         {
        //           max: 20,
        //           message:
        //             'Serial number is limited to 20 characters. ',
        //         },
        //       ],
        //     })(<Input disabled={!opts.editMode} />)}
        //   </Form.Item>
        // ) : null;
      }).filter(f => !!f),
    ]
  };

  trackEvent = (event = '', data = {}) => {
    trackEvent(event, { ...data, equipment: this.state.equipment });
  };
  componentDidMount = () => {
    const { state } = this.props.location;
    const equipment = get(state, 'equipment');

    if (equipment) {
      this.setState(
        {
          equipment,
          model: get(state, 'model'),
          serviceOrg: get(state, 'serviceOrg'),
          dentalOrg: get(state, 'dentalOrg'),
          serviceRecord: get(state, 'serviceRecord'),
          editMode: get(state, 'editMode', this.state.editMode),
        },
        () => {
          if (this.state.serviceRecord) {
            this.trackEvent(eventTypes.eq_service_view);
          }
          this.setFieldsOriginal();
        }
      );
    }

    var e = document.getElementById('ContentContainer');
    if (e && e.scrollTop) 
      e.scrollTop = 0;
  };
  confirmDisclaimer = () => {
    return new Promise<void>((resolve, reject) => {
      const hasService =
        this.getSelectedServices().indexOf(get(
          serviceType,
          'hp_general.name',
          ''
        ) as string) > -1;

      if (hasService && this.state.acceptDisclaimer) {
        this.props.app.modal.confirm({
          title: 'Reset General Runtime',
          content:
            'Selecting this option will reset the handpiece runtime to clear the general maintenance alert',
          onCancel: () => reject(new Error('Save cancelled.')),
          onOk: () => resolve(),
        });
      } else {
        resolve();
      }
    });
  };
  getSelectedServices = () => {
    return this.props.form.getFieldValue('services') || [] as string[];
  };
  submit = async (e: MouseEvent | FormEvent) => {
    if (userHasRole([6, 7], this.props.sState)) {
      this.props.app.modal.warning({
        title: 'Functionality Not Available',
        content:
          //tslint:disable-next-line: max-line-length
          'Your user account does not have access to complete this functionality.\
          If you feel that this is incorrect, please contact your company Admin.',
        onOk: () => {
          return 0;
        },
      });

      return;
    }

    stopProp(e);
    if (this.state.loading) {
      return;
    }

    const services = this.getSelectedServices();
    if (services.length <= 0) {
      this.props.app.modal.warning({
        title: 'Select a Service',
        content: `Select at least one service to add a service record.`,
        onOk: () => {
          return 0;
        },
      });

      return;
    }

    this.state.loading = true;
    this.setState(this.state);

    this.props.form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        message.error(error);
        return this.setState({ loading: false });
      }

      try {
        await this.confirmDisclaimer();

        const { sState } = this.props;
        const { serviceRecord, equipment } = this.state;

        const serials: { num: number | string; value?: string }[] = [];
        let defaultnote = '';
        const services = flatten(this.getSelectedServices().map((s: any) => {
          const st = findServiceInTypes(s);
          const hasVal = get(
            values,
            `${SERIAL}${get(st, 'key')}`,
            SERIALNOTNECESSARY
          );
          if (st && st.showSerial && st.heads) {
            serials.push({
              num: st.heads,
              value: hasVal,
            });
          }
          const comps = get(st, 'comps');
          if (comps) { 
            defaultnote = get(st, 'name', '');
            return comps.map(k => ({ [k]: hasVal })); 
          }
          return {
            [get(st, 'key', NOKEY)]: hasVal,
          };
        }));

        const sdate = moment(values.serviceDate).toISOString();
        const deviceId = get(this.state.equipment, 'deviceId', '');
        const serv = new _Service({
          ...(serviceRecord || {}),
          coreid: deviceId,
          techName: values.techName,
          note: (() => { 
            if (values.note || !defaultnote) { 
              return values.note;
            } else {
              return defaultnote;
            }
          })(),
          serviceDate: sdate,
          published_at: sdate,
          services,
        } as Service);

        const me: any = get(sState, 'auth.user.userId', '');
        serv.createdBy = me;
        serv.updatedBy = me;

        let commands: {
          cmd: string;
          callback?: (success: boolean) => void;
        }[] = [];

        const buildCommand = (
          s: {},
          setSN = false
        ): { cmd: string; callback?: (success: boolean) => void }[] => {
          const k = Object.keys(s)[0];
          const key = (k || '')
            .replace('comp_', '')
            .replace('vac_', '')
            .replace('hp_', '');
          const splitkey = key.split(',');
          const val = get(s, k);
          const stype = get(serviceType, k, {});

          if (get(stype, 'dontSend')) {
            return [];
          } else if (setSN) {
            return [{ cmd: `set_sn,${key},${val}` }];
          } else if (size(splitkey) > 1) {
            return splitkey.map(skey => {
              return {
                cmd: `reset_runtime,${skey}${
                  val !== SERIALNOTNECESSARY ? ',' + val : ''
                }`,
              };
            });
          } else {
            const e: Equipment | undefined = get(this.state, 'equipment');
            if (e && isHandpiece(e)) {
              //DEN-908
              return [
                {
                  cmd: `{ "cmd": "reset_mtr_runtime" }`,
                  callback: success => {
                    if (success) {
                      // DEN-960
                      sendDeviceCommand(
                        e['deviceId'],
                        {
                          arg:
                            '{"cmd":"sensor_instant", "interval": 1, "duration": 1}',
                        },
                        { showErrorToast: true },
                        true
                      );
                    }
                  },
                },
              ];
            } else {
              if (key === NOKEY) { return [] }
              return [
                {
                  cmd: `reset_runtime,${key}${
                    val !== SERIALNOTNECESSARY ? ',' + val : ''
                  }`,
                },
              ];
            }
          }
        };

        if (serviceRecord) {
          const sr = new _Service(serviceRecord);
          serv.createdAt = sr.createdAt;
          serv.createdBy = sr.createdBy;
          const pastServices = get(serviceRecord, 'services', []);
          const pastMap: { [key: string]: string } = {};
          pastServices.map(
            ps => (pastMap[Object.keys(ps)[0]] = ps[Object.keys(ps)[0]])
          );
          commands = flatten(
            serv.services
              .map(s => {
                const k = Object.keys(s)[0];
                if (s[k] === !SERIALNOTNECESSARY) {
                  return [];
                } else {
                  const hasPast = pastMap[k];
                  const notSame = hasPast !== s[k];

                  if (hasPast && notSame) {
                    return buildCommand(s, true);
                  } else if (!hasPast) {
                    return buildCommand(s);
                  } else {
                    return [];
                  }
                }
              })
              .filter(f => f)
          ) as { cmd: string; callback?: (success: boolean) => void }[];
        } else {
          commands = flatten(serv.services.map(s => buildCommand(s))).filter(
            f => f
          ) as { cmd: string; callback?: (success: boolean) => void }[];
        }

        if (commands.length > 0) {
          await Promise.all(
            commands.map(
              async command =>
                await sendDeviceCommand(
                  deviceId,
                  { arg: command.cmd },
                  { showErrorToast: true },
                  isHandpiece(new _Equipment(equipment)),
                  command.callback
                )
            )
          );
        }

        if (serials.length > 0) {
          const equip = new _Equipment(equipment);
          serials.map(s => {
            if (s.value) {
              equip[`mSerial${s.num}`] = s.value;
            }
          });
          set(equip, 'updatedAt', moment().toISOString());
          set(equip, 'updatedBy', me);
          await createOrUpdateEquipment(equip);
        }

        if (isHandpiece(new _Equipment(equipment)) && serv.services.length) {
          const hpMotorIndex = serv.services.findIndex(s =>
            Object.keys(s).includes('hp_motor')
          );
          if (hpMotorIndex >= 0) {
            const [, hours] = handpieceIsDueForService(
              sState,
              new _Equipment(equipment),
              []
            );
            set(
              serv.services[hpMotorIndex],
              'currentRuntime',
              hours > 0 ? hours * 60 : 0
            ); //store as minutes.
          }
        }
        await createService(serv);
        message.success(
          serviceRecord
            ? 'Service record has been updated'
            : 'Service record has been added'
        );

        this.trackEvent(
          serviceRecord
            ? eventTypes.eq_service_update
            : eventTypes.eq_service_add,
          serviceRecord
            ? {
                old: serviceRecord,
                new: serv,
              }
            : { serviceRecord: serv }
        );

        this.setState(
          {
            loading: false,
          },
          this.goBack
        );
      } catch (err) {
        const ee = get(err, 'error', err.message);
        const showError = (() => {
          if (ee === 'Save cancelled.') {
            return false;
          }
          return true;
        })();

        if (showError) {
          message.error('Save failed: ' + ee);
        }
        return this.setState({
          loading: false,
        });
      }
    });
  };
  setFieldsOriginal = () => {
    const { toDisplay, serviceRecord, equipment } = this.state;
    const {
      form: { setFieldsValue },
      sState,
    } = this.props;
    // AER-1465: state can be undefined if the user lands here without an
    // explicit navigation (refresh / deep-link). Avoid the destructure-on-
    // undefined that produced the white screen.
    const serviceTypes = get(this.props, 'location.state.serviceTypes', []);

    let useMaint: { [x: string]: string }[] = [];

    const setFields = () => {
      if (serviceRecord) {
        toDisplay().map((item: any) => {
          let val: any = get(serviceRecord, item.var);

          if (item.var === 'services') {
            // AER-1601: emit chips as `{value, text}` (the shape the shared
            // Form's MultiSelectSearch produces from onTagsChange). The
            // trailing setFieldsValue feeds this into the form so Form reads
            // it via `form.getFieldValue(item.var)`.
            val = (val || []).map((s: any) => {
              const key = Object.keys(s)[0];
              return { value: key, text: get(serviceType, [key, 'name']) };
            }).filter((f: any) => !!f.text);
            // AER-1743: a `this.MSearch.setState({ selected: item.selected })`
            // used to follow. `MSearch` was never assigned — nothing hands this
            // component a ref to the control — so the branch never ran, and it
            // would have pushed the wrong list if it had (`item.selected` is
            // the router-state seed, not the record's services). The chips are
            // re-seeded by the `setFieldsValue` below, which the shared Form
            // reads back through `form.getFieldValue`.
            this.forceUpdate();
          } else {
            setFieldsValue({
              [item.var]:
                item.type === 'datepicker'
                  ? moment(val)
                  : val === SERIALNOTNECESSARY
                  ? ''
                  : val,
            });
          }

          setFieldsValue({
            [item.var]:
              item.type === 'datepicker'
                ? moment(val)
                : val === SERIALNOTNECESSARY
                ? ''
                : val,
          });
        });
      } else {
        const maint = getEquipMaint(equipment, sState);
        const data = get(maint, '0.data', {});
        const selected = size(serviceTypes)
          ? serviceTypes.map((s: { name: string }) => s.name)
          : [];
        for (let k in data) {
          const item = data[k];
          if (Array.isArray(item) && item[0] === true) {
            const oldFilters = [
              'comp_airfilter',
              'comp_coafilter',
              'comp_partfilter',
            ];
            if (oldFilters.includes(k)) k = 'comp_filterpack';
            // AER-1744: resolve the catalog key ONCE, then use it for every
            // downstream write. Alerts arrive either already prefixed
            // (`comp_filterpack`, older compressors) or bare (`filterpack`),
            // where the prefix is whichever of `comp_`/`vac_` actually names a
            // service — the same probe, in the same order, that
            // `getTriggeredMaint` uses to build the overdue list, so the two
            // cannot disagree about which services are due. Previously only the
            // name lookup did this probe: `useMaint` and the chip list were
            // seeded from the bare `k` and from the friendly name respectively,
            // and neither is what the dropdown de-duplicates against — it
            // compares catalog keys (multi-select-search.tsx:161-165). So an
            // overdue service stayed selectable and could be written to the
            // record twice, and on the bare-key branch the chip rendered a raw
            // key. Pushing the catalog key fixes both: the chip still reads
            // "Filter Pack" because MultiSelectSearch resolves the label from
            // `dataSource` (`textFromDataSource`, AER-1696).
            const isOldComp = k.indexOf('comp_') > -1;
            const catalogKey = (isOldComp
              ? [k]
              : [`comp_${k}`, `vac_${k}`]
            ).find(c => !!get(serviceType, [c, 'name']));
            if (catalogKey) {
              useMaint.push({ [catalogKey]: '' });
              selected.push(catalogKey);
            }
          }
        }
        // AER-1465: previously this line set the 'services' field to the
        // column descriptor object (its = toDisplay().find(...)) instead of
        // the selected list, which caused `.map`/`.indexOf` on the form value
        // to throw later (white screen on Add Service Record).
        setFieldsValue({
          services: uniq(selected),
        });
      }
    };
    setFields();

    setTimeout(() => {
      const selectedServices = this.getSelectedServices();
      if (
        selectedServices.length > 0 &&
        (serviceRecord || useMaint.length > 0)
      ) {
        const services =
          useMaint.length > 0 ? useMaint : get(serviceRecord, 'services', []);
        services.map(s => {
          const key = Object.keys(s)[0];
          // AER-1733 / AER-1744: seed by the CATALOG key — the same identity
          // the `Serial - X` field is built from above and the one submit
          // reads back. Both channels feeding `services` here already speak
          // catalog keys (the persisted record always has; `useMaint` since
          // AER-1744), and running them through the same resolver the other
          // two sites use keeps a future channel from drifting apart again.
          // The dropped `serviceType[key]` lookup on this line was unused, and
          // was `undefined` on the bare-key branch anyway.
          const serialKey = get(findServiceInTypes(key), 'key', key);
          setFieldsValue({
            [`${SERIAL}${serialKey}`]:
              s[key] === SERIALNOTNECESSARY ? '' : s[key],
          });
        });
      }
      this.forceUpdate();
    }, 500);
  };
  goBack = () => {
    this.props.history.goBack();
  };
  goToBillingHistory = (equipmentId: string) => {};
  cancel = () => {
    const { editMode, serviceRecord } = this.state;
    if (serviceRecord && editMode) {
      // AER-1601: Cancel on an existing record drops the user back into the
      // read-only view. `setFieldsOriginal()` re-seeds every field from the
      // persisted record — technician name, date and note directly, and the
      // service chips by writing the record's services back into the form
      // store. MultiSelectSearch picks that up in `componentDidUpdate`
      // (multi-select-search.tsx:147-157): the shared Form passes the form
      // value as `selected`, and a changed `selected` prop rebuilds the chip
      // list from it, so the control cannot carry chips across a mode flip.
      // AER-1743: a `msearchKey` counter was bumped here and on Edit, with a
      // comment claiming it remounted MultiSelectSearch to prevent exactly
      // that. It was never read — not passed as a React `key`, not passed to
      // the shared Form at all — so no remount ever happened; the reseed
      // described above is what has always done the work. Removed rather than
      // wired up: a real remount would reapply every prop unconditionally,
      // including `disabled`, which is the AER-1732 failure this screen was
      // just fixed for.
      this.setFieldsOriginal();
      return this.setState({ editMode: false });
    }
    this.goBack();
  };
  render() {
    const {
      loading,
      equipment,
      model,
      toDisplay,
      editMode,
      serviceRecord,
      acceptDisclaimer,
    } = this.state;
    const { isPhoneBreak } = get(this.props, 'sState.dash.view') as any;
    const { sState, form: { Form } } = this.props;
    const services = this.getSelectedServices();
    const config = getEquipConfig(equipment, sState);
    return (
      <div css={css(styles.container)}>
        <div css={css(SharedStyles.row, `width: 100%;`)}>
          <Link onClick={this.goBack}>{'< Back'}</Link>
        </div>

        <div css={css(styles.container, `flex-direction: row;`)}>
          {equipment && !isPhoneBreak && (
            <Sidebar
              equipment={(equipment as unknown) as Equipment}
              model={model}
              // @ts-ignore: config from getEquipConfig is loosely typed
              config={config}
              showHeads={true}
              goToBillingHistory={this.goToBillingHistory}
            />
          )}

          <div css={css(styles.mainContainer)}>
            <div css={css(SharedStyles.row)}>
              <FormTitle size={'16px'}>
                {`${
                  serviceRecord && editMode ? 'Edit' : editMode ? 'Add' : ''
                } Service Record`}
              </FormTitle>
            </div>

            <Form
              css={css('max-width: 500px;')}
              layout="vertical"
              // AER-1714: the `Serial - X` fields are derived here, in the owner's
              // render, from `services` (read off the form store). The shared Form
              // re-rendering on its own is not enough — `items` is built by this
              // component, so the owner has to re-render for a new selection to
              // produce a new field. Wiring `forceUpdate` into the shared Form's
              // change notification makes that happen on selection instead of
              // whenever some unrelated update next repaints this component.
              forceUpdate={this}
              onSubmit={this.submit}
              items={toDisplay({ services, editMode })}
            >

              {services.indexOf(get(
                serviceType,
                'hp_general.name',
                ''
              ) as string) > -1 && (
                <div css={css(`margin-bottom: 10px;`)}>
                  <Checkbox
                    checked={acceptDisclaimer}
                    onChange={e =>
                      this.setState({ acceptDisclaimer: e.target.checked })
                    }
                  >
                    Reset General Runtime
                  </Checkbox>
                </div>
              )}

              {(!serviceRecord || editMode) && (
                <Button
                  title={'Cancel'}
                  disabled={loading}
                  css={css`
                    margin-right: 10px;
                  `}
                  outline={true}
                  onClick={this.cancel}
                />
              )}
              {!editMode && (
                <Button
                  title={'Edit'}
                  loading={loading}
                  // AER-1743: this used to bump a `msearchKey` counter as well,
                  // to "start MultiSelectSearch fresh". Nothing read it — see
                  // `cancel()` above. The chips already match the record here,
                  // because Cancel re-seeds them on the way out of edit mode.
                  onClick={() => this.setState({ editMode: !editMode })}
                />
              )}
              {editMode && (
                <Button
                  title={serviceRecord ? 'Save' : 'Add'}
                  loading={loading}
                  onClick={this.submit}
                />
              )}
            </Form>
          </div>
        </div>
      </div>
    );
  }
}

export const ManageServiceRecordComponent = WithAppConfig(WithForm(withRouter(
  _ManageServiceRecordComponent
)));
